import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Users, Lock, Heart, UserPlus, Home, Sparkles, 
  TrendingUp, TrendingDown, Activity, Minus
} from 'lucide-react';
import { useRelationalCorrelations } from '@/hooks/useRelationalCorrelations';
import { useSubscription, PRODUCTS } from '@/contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const DATE_RANGES = [
  { value: '14', label: 'Last 2 weeks' },
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
];

interface PulseEntry {
  date: string;
  relational_wellbeing_score: number | null;
  partner_connection: number | null;
  family_harmony: number | null;
  friend_support: number | null;
  self_compassion: number | null;
  community_belonging: number | null;
  displayDate: string;
}

export function RelationalPatternsExplorer() {
  const { correlations, summary, isLoading: correlationsLoading, error } = useRelationalCorrelations(60);
  const { productId, hasPremiumAccess } = useSubscription();
  const navigate = useNavigate();
  
  const [dateRange, setDateRange] = useState<string>('30');
  const [pulseData, setPulseData] = useState<PulseEntry[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const isSanctuary = productId === PRODUCTS.MASTERY || hasPremiumAccess();

  // Fetch pulse data for chart
  useEffect(() => {
    const fetchPulseData = async () => {
      if (!isSanctuary) return;
      
      setIsLoadingData(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoadingData(false);
        return;
      }

      const startDate = format(subDays(new Date(), parseInt(dateRange)), 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('relationship_pulse_checkins')
        .select('*')
        .eq('user_id', user.id)
        .gte('checkin_date', startDate)
        .order('checkin_date', { ascending: true });

      if (!error && data) {
        const formattedData = data.map(row => ({
          date: row.checkin_date,
          relational_wellbeing_score: row.relational_wellbeing_score,
          partner_connection: row.partner_connection,
          family_harmony: row.family_harmony,
          friend_support: row.friend_support,
          self_compassion: row.self_compassion,
          community_belonging: row.community_belonging,
          displayDate: format(new Date(row.checkin_date), 'MMM d')
        }));
        setPulseData(formattedData);
      } else {
        setPulseData([]);
      }
      
      setIsLoadingData(false);
    };

    fetchPulseData();
  }, [dateRange, isSanctuary]);

  // Gate behind Sanctuary subscription
  if (!isSanctuary) {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-xl">Relational Patterns & Connection</CardTitle>
          <CardDescription>
            Sanctuary members get access to relational correlations - understanding how your sense of connection with yourself and others affects your mood, energy, and nervous system.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Users className="w-4 h-4" />
              <span>Loneliness ↔ Mood correlations</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Heart className="w-4 h-4" />
              <span>Partner/Family ↔ Emotional stability</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <UserPlus className="w-4 h-4" />
              <span>Friend Support ↔ Anxiety patterns</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="w-4 h-4" />
              <span>Self-Compassion ↔ Wellbeing</span>
            </div>
          </div>
          <Button onClick={() => navigate('/subscription')} className="w-full">
            <Sparkles className="w-4 h-4 mr-2" />
            Upgrade to Sanctuary
          </Button>
        </CardContent>
      </Card>
    );
  }

  const hasPulseData = pulseData.length > 0;
  const getTrendIcon = () => {
    if (!summary?.relational_wellbeing_trend) return <Minus className="w-4 h-4" />;
    if (summary.relational_wellbeing_trend.direction === 'improving') return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    if (summary.relational_wellbeing_trend.direction === 'declining') return <TrendingDown className="w-4 h-4 text-rose-500" />;
    return <Minus className="w-4 h-4 text-muted-foreground" />;
  };

  const getTrendLabel = () => {
    if (!summary?.relational_wellbeing_trend) return 'No trend data';
    const avg = summary.relational_wellbeing_trend.average;
    if (avg >= 7) return 'Strong connection';
    if (avg >= 5) return 'Moderate connection';
    return 'Lower connection';
  };

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Relational Patterns & Connection
          </CardTitle>
          <CardDescription>
            See how feeling supported, lonely, or connected links with your mood, energy, and nervous system over time.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary Stats */}
          {summary?.relational_wellbeing_trend && (
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
              <div className="flex items-center gap-3">
                {getTrendIcon()}
                <div>
                  <p className="text-sm font-medium">Relational Wellbeing Trend</p>
                  <p className="text-xs text-muted-foreground">{getTrendLabel()}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-light text-primary">
                  {summary.relational_wellbeing_trend.average.toFixed(1)}
                </p>
                <p className="text-xs text-muted-foreground">30-day avg</p>
              </div>
            </div>
          )}

          {/* Date Range Selector */}
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGES.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Time Series Chart */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-muted">
                <Activity className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">Relational Wellbeing Over Time</CardTitle>
                <CardDescription className="text-xs">
                  {DATE_RANGES.find(r => r.value === dateRange)?.label}
                </CardDescription>
              </div>
            </div>
            {hasPulseData && (
              <Badge variant="secondary" className="text-xs">
                {pulseData.length} entries
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingData ? (
            <Skeleton className="h-48 w-full" />
          ) : hasPulseData ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={pulseData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="displayDate" 
                    tick={{ fontSize: 10 }}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    domain={[0, 10]}
                    tick={{ fontSize: 10 }}
                    className="text-muted-foreground"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="relational_wellbeing_score" 
                    name="Overall"
                    stroke="hsl(var(--primary))"
                    strokeWidth={3}
                    dot={{ fill: 'hsl(var(--primary))', strokeWidth: 0, r: 4 }}
                    connectNulls
                  />
                  <Line 
                    type="monotone" 
                    dataKey="self_compassion" 
                    name="Self-Compassion"
                    stroke="hsl(var(--chart-1))"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    dot={false}
                    connectNulls
                  />
                  <Line 
                    type="monotone" 
                    dataKey="friend_support" 
                    name="Friend Support"
                    stroke="hsl(var(--chart-2))"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    dot={false}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center">
              <div className="text-center p-6 rounded-lg bg-muted/30 max-w-md">
                <Users className="w-8 h-8 mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground mb-2">
                  No relational pulse data logged yet.
                </p>
                <p className="text-xs text-muted-foreground">
                  Use the Relational Pulse Check to start tracking how connected you feel.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reflection Card */}
      <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Heart className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h4 className="text-sm font-medium mb-1">Understanding Relational Patterns</h4>
              <p className="text-xs text-muted-foreground">
                Relationships are complex, shaped by attachment history, nervous system patterns, and current life circumstances. 
                These correlations show how your sense of connection moves alongside your mood - neither causes the other, 
                but they're often part of the same picture. Trust your own experience.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Correlations Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Relational Correlations
          </CardTitle>
          <CardDescription>
            Patterns emerging from your relational and emotional data
          </CardDescription>
        </CardHeader>
        <CardContent>
          {correlationsLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : correlations.length > 0 ? (
            <div className="space-y-3">
              {correlations.map((correlation, index) => (
                <div 
                  key={index}
                  className="p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-muted">
                      <Users className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-medium">
                          {correlation.metric_a} ↔ {correlation.metric_b}
                        </h4>
                        <Badge variant="outline" className="text-xs">
                          {correlation.sample_size} days
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {correlation.interpretation}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center p-6 rounded-lg bg-muted/30">
              <Users className="w-8 h-8 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground mb-1">
                No relational correlations detected yet.
              </p>
              <p className="text-xs text-muted-foreground">
                Keep logging your relational pulse and daily check-ins - patterns will emerge as data accumulates.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
